import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ChildActivationEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { CspStyleNonceService } from '@app/csp-style-nonce.service';
import { environment } from '@env';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BroadcasterService } from 'ng-broadcaster';
import { distinctUntilChanged, filter, Subject, take, takeUntil } from 'rxjs';
import {
    CommonUtilsService,
    CompletedSwitchToOrganization,
    GetApplicationProperties,
    getApplicationProperties$,
    GetSessionDetails,
    Organization,
    RedirectToBase,
    SetCurrentOrganization,
    SetLogoHref,
    SetSessionDetails,
    StoreService,
    UserPreferencesService,
    UtilsService,
} from 'taxilla-library';

import { RootScope } from '../../services/rootscope.service';
import { VersionCheckService } from '../../services/version-check/version-check.service';

@Component({
    selector: 'app-main-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit {
    title = 'taxilla';
    isPostLogin: any;
    RouteRefreshSubscription: any;
    hasOrgId: string;
    hideBody = false;
    hideHeader = false;
    public supportSystem = environment.supportSystem;
    public isNewUI: boolean;
    private unSubscribe = new Subject<void>();
    public inGuestView = location.href.includes(`${environment['encomply-ui']}/collaboration`);

    constructor(
        protected _breakpointObserver: BreakpointObserver,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        public R: RootScope,
        private _utils: UtilsService,
        private _commonUtils: CommonUtilsService,
        private _broadcaster: BroadcasterService,
        public versionCheckService: VersionCheckService,
        private _store: StoreService,
        private store$: Store,
        private actions$: Actions,
        private cspStyleNonceService: CspStyleNonceService,
        private userPreferences: UserPreferencesService
    ) {
        const skin = new URL(window.location.href).searchParams?.get('skin');
        const hideSkinChange = environment?.hideSwitchSkin;
        const defaultNewUISkin = environment?.defaultNewUISkin;
        if (hideSkinChange && defaultNewUISkin) {
            this._commonUtils.setCookie('new-ui', defaultNewUISkin ? 'true' : 'false');
        } else if (skin?.length > 0) {
            const currentSkin = this._commonUtils.getCookie('new-ui');
            if (currentSkin !== (skin === 'new' ? 'true' : 'false')) {
                this._commonUtils.setCookie('new-ui', skin === 'new' ? 'true' : 'false');
            }
        }
        if (environment?.defaultNewUISkin || environment?.['environment']?.defaultNewUISkin) {
            const onNewUI = this._commonUtils.getCookie('new-ui');
            (onNewUI === undefined || onNewUI === null) && this._commonUtils.setCookie('new-ui', 'true');
        }
        this.setBodyContext(this._router.url);
        const origin = window.location.origin;
        if (this._commonUtils.isBrowserIE()) {
            this.addStorageChangeEvent();
        }
        if (origin) {
            this.setTitle();
        }
        this.isNewUI = this._commonUtils.getCookie('new-ui') === 'true';
        const organization = this._commonUtils.getFromStorage('currentOrganization');
        organization?.id?.length > 0 && this.store$.dispatch(SetCurrentOrganization({ organization }));
        if (this.userPreferences) {
            console.log('[UserPreferences]');
        }
    }

    setTitle = () => {
        this._utils.setTitle(environment.documentTitle);
        let faviconHref = `assets/images/favicon.ico?v=${Date.now()}`;
        // Set dynamic favicon based on logoHref from environment
        if (environment.customFavicon) {
            // Remove existing favicon links
            const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
            existingFavicons.forEach(link => link.remove());
            faviconHref = `assets/images/${environment.logoHref}favicon.ico?v=${Date.now()}`;
        }
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.type = 'image/x-icon';
        newFavicon.href = faviconHref;
        document.head.appendChild(newFavicon);
    };

    addStorageChangeEvent = () => {
        window.onstorage = (e: any) => {
            console.log(e?.key);
        };
    };

    triggerResponsiveness = () => {
        if (this.R.device !== 'device-desktop' && !this.isPostLogin) {
            this.R.responsive = 'app-responsive';
        } else {
            this.R.responsive = '';
        }
    };

    refreshCurrentRoute = () => {
        const currentUrl = this._router.url;
        (url => {
            this.RouteRefreshSubscription = this._router.events.pipe(takeUntil(this.unSubscribe)).subscribe(event => {
                if (event instanceof ChildActivationEnd) {
                    this._router.navigate([url]);
                }
            });
            setTimeout(() => {
                this.RouteRefreshSubscription?.unsubscribe?.();
            }, 1000);
        })(currentUrl);
    };

    private logout = () => {
        this._commonUtils.setInStorage('session', false);
        this.hasOrgId = undefined;
        if (this._router.url === '/login') {
            this.refreshCurrentRoute();
        }
        // $('.modal').modal('hide');
    };

    private checkIfPostLogin = () => {
        const routes = window.location.pathname.split('/');
        const orgIndex = routes.indexOf('organizations');
        this.hasOrgId = orgIndex > -1 && routes[orgIndex + 1];
    };

    private hideBodyIfNotInContext = (href: string) => {
        href = location.origin + href;
        const isInEnReport = href.indexOf(environment['enreport-ui']) > -1;
        const isInEnInvoice =
            href.indexOf(environment['eninvoice-ui']) > -1 || href.indexOf(environment['encomply-ui'] + '/collaboration') > -1;
        const isInEnCollab = href.indexOf(environment['encollab-ui']) > -1;
        const isInEnReconcile = href.indexOf(environment['enreconcile-ui']) > -1;
        const isInGstFiling = href.indexOf(environment['gst-filing']) > -1;
        const isInCustomUI = href.indexOf(environment['custom']) > -1;
        const isInEnComply = !isInEnReport && !isInEnInvoice && !isInEnCollab && !isInEnReconcile && !isInGstFiling && !isInCustomUI;
        const bodyClass = document.querySelector('body').getAttribute('class') || '';
        if (isInEnComply && bodyClass.indexOf('enComplyMainContainer') === -1) {
            this.hideBody = true;
        } else if (isInEnReport && bodyClass.indexOf('enReportMainContainer') === -1) {
            this.hideBody = true;
        } else if (isInEnInvoice && bodyClass.indexOf('enInvoiceMainContainer') === -1) {
            this.hideBody = true;
        } else if (isInEnCollab && bodyClass.indexOf('enCollabMainContainer') === -1) {
            this.hideBody = true;
        } else if (isInEnReconcile && bodyClass.indexOf('enReconcileMainContainer') === -1) {
            this.hideBody = true;
        } else if (isInGstFiling && bodyClass.indexOf('gstFilingMainContainer') === -1) {
            this.hideBody = true;
        }
    };

    private setBodyContext = (href: string) => {
        href = location.origin + href;
        const isInAuthUI = href.indexOf(environment['auth-ui']) > -1;
        const isInEnReport = href.indexOf(environment['enreport-ui']) > -1;
        const isInEnInvoice =
            href.indexOf(environment['eninvoice-ui']) > -1 || href.indexOf(environment['encomply-ui'] + '/collaboration') > -1;
        const isInEnCollab = href.indexOf(environment['encollab-ui']) > -1;
        const isInEnRecon = href.indexOf(environment['enreconcile-ui']) > -1;
        const isInGstFiling = href.indexOf(environment['gst-filing']) > -1;
        const isInCustomUI = href.indexOf(environment['custom']) > -1;
        const isInEnComply =
            !isInEnReport && !isInEnInvoice && !isInAuthUI && !isInEnCollab && !isInEnRecon && !isInGstFiling && !isInCustomUI;
        const bodyClass = document.querySelector('body').getAttribute('class') || '';
        const onNewUI = this._commonUtils.getCookie('new-ui') === 'true';
        if (isInAuthUI && bodyClass.indexOf('autheticationContainer') === -1) {
            this._commonUtils.setBodyContext('autheticationContainer enComplyMainContainer');
        } else if (
            isInEnComply &&
            (bodyClass.indexOf('enComplyMainContainer') === -1 || bodyClass.indexOf('autheticationContainer') > -1)
        ) {
            this._commonUtils.setBodyContext('enComplyMainContainer');
        } else if (isInEnComply && onNewUI && bodyClass.indexOf('onNewUI') === -1) {
            this._commonUtils.setBodyContext('enComplyMainContainer');
        } else if (isInEnReport && bodyClass.indexOf('enReportMainContainer') === -1) {
            this._commonUtils.setBodyContext('enReportMainContainer');
        } else if (isInEnInvoice && bodyClass.indexOf('enInvoiceMainContainer') === -1) {
            this._commonUtils.setBodyContext('enInvoiceMainContainer');
        } else if (isInEnCollab && bodyClass.indexOf('enCollabMainContainer') === -1) {
            this._commonUtils.setBodyContext('enCollabMainContainer');
        } else if (isInEnRecon && bodyClass.indexOf('enReconMainContainer') === -1) {
            this._commonUtils.setBodyContext('enReconMainContainer');
        } else if (isInGstFiling && bodyClass.indexOf('gstFilingMainContainer') === -1) {
            this._commonUtils.setBodyContext('gstFilingMainContainer');
        }
        this.hideBody = false;
    };

    protected getApplicationProperties = (baseUrl: string) => {
        if (baseUrl === 'mapro') {
            this.store$.dispatch(GetApplicationProperties());
        }
    };

    setApplicationProperties = response => {
        if (response?.['analytics.service.types']?.length > 0) {
            this._commonUtils.analyticsServiceTypes = response['analytics.service.types'];
        }
    };

    private setTimeZoneOffset = (organization: Organization) => {
        const tz = organization?.timeZone;
        const serverTimeZoneOffset = organization?.timeZoneOffset || this._commonUtils.getFromStorage('serverTimeZoneOffset');
        if (!tz || serverTimeZoneOffset) {
            return;
        }
        this._utils.setTimeZoneOffset({
            serverTimeZoneOffset,
            tz,
        });
    };

    ngOnInit() {
        // Initialize CSP nonce service first
        this.cspStyleNonceService.initialize();

        // On a region switch the browser lands directly on the home/encomply route
        // (not on /redirect), so RedirectComponent never loads and GetSessionDetails
        // is never dispatched — /info is never called.
        // Dispatch it here for every non-redirect boot so that /info always fires
        // before application-properties (which needs a valid session/token).
        if (!window.location.pathname.includes('/redirect') && !window.location.pathname.includes('/auth')) {
            this.store$.dispatch(GetSessionDetails({ noAlerts: true, noSwitch: true }));
        }

        this.store$.dispatch(SetLogoHref({ logoHref: environment.logoHref }));
        this._breakpointObserver
            .observe(['(orientation: portrait)'])
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(result => {
                if (this.R.orientation !== 'orientation-portrait' && result.matches) {
                    this.R.orientation = 'orientation-portrait';
                }
                this.triggerResponsiveness();
            });

        this._breakpointObserver
            .observe(['(orientation: landscape)'])
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(result => {
                if (this.R.orientation !== 'orientation-landscape' && result.matches) {
                    this.R.orientation = 'orientation-landscape';
                }
                this.triggerResponsiveness();
            });

        this._breakpointObserver
            .observe(['(min-width: 1200px)'])
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(result => {
                result.matches && (this.R.device = 'device-desktop');
                this.triggerResponsiveness();
            });

        this._breakpointObserver
            .observe(['(max-width: 1199px)'])
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(result => {
                result.matches && (this.R.device = 'device-handset');
                this.triggerResponsiveness();
            });

        this._router.events.pipe(takeUntil(this.unSubscribe)).subscribe(event => {
            if (event instanceof NavigationStart) {
                this.hideBodyIfNotInContext(event.url);
            } else if (event instanceof NavigationEnd) {
                this.setBodyContext(event.url);
                this.isPostLogin =
                    this._activatedRoute.snapshot.children &&
                    this._activatedRoute.snapshot.children[0] &&
                    this._activatedRoute.snapshot.children[0].data &&
                    this._activatedRoute.snapshot.children[0].data.secured;
                this.hideHeader = !!this._activatedRoute.snapshot.children?.[0]?.data?.['hideHeader'];
                this.triggerResponsiveness();
                this.checkIfPostLogin();
            }
        });

        this._broadcaster
            .on('logout')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((data: { noRedirect: boolean; hidePopup: boolean }) => {
                this._store.logout();
                this.logout();
                this._utils.postLogout(data && (data.noRedirect !== undefined ? !data.noRedirect : false), data && data.hidePopup);
                this.setTitle();
            });

        this._broadcaster
            .on('switchingOrganization')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(() => {
                this._store.clearLoading();
            });

        this.actions$
            .pipe(
                ofType(CompletedSwitchToOrganization),
                filter(data => data.switched)
            )
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(data => {
                this._commonUtils.setInStorage('currentOrganizationId', data.organization?.id);
                data.organization && this._commonUtils.setInStorage('currentOrganization', data.organization);
                this.setTimeZoneOffset(data.organization);
            });
        this.actions$
            .pipe(
                ofType(SetSessionDetails),
                filter(data => !!data.user?.id),
                distinctUntilChanged((a, b) => a.user?.id === b.user?.id && a?.currentOrg?.id === b?.currentOrg?.id)
            )
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(data => {
                this._store.clearLoading();
                this._commonUtils.setInStorage('user', data.user);
            });

        // Trigger application-properties only ONCE — after the first successful
        // session (info) response so that new-region cookies are valid.
        this.actions$
            .pipe(
                ofType(SetSessionDetails),
                filter(data => !!data.user?.id),
                take(1)
            )
            .subscribe(() => this.getApplicationProperties('mapro'));

        this._broadcaster
            .on('session')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((event: boolean) => {
                this.R.session.next(event);
            });

        this._broadcaster
            .on('loggingIn')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((event: boolean) => {
                this.R.loggingIn = event;
            });

        setTimeout(() => {
            this.triggerResponsiveness();
            this.versionCheckService.initVersionCheck();
        });
        this._broadcaster
            .on('setStoreContext')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(() => this._store.setOrganizationId());

        this.store$
            .select(getApplicationProperties$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(res => res !== undefined)
            )
            .subscribe({
                next: res => {
                    this.setApplicationProperties(res);
                },
            });

        this.actions$
            .pipe(ofType(RedirectToBase))
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(() => {
                this._utils.navigateToOrganization();
            });
    }

    ngOnDestroy() {
        this.cspStyleNonceService.destroy();
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
